import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    Timestamp,
    onSnapshot,
    deleteDoc
} from 'firebase/firestore';
import {
    ref,
    uploadBytes,
    getDownloadURL,
    deleteObject,
    listAll,
    getMetadata
} from 'firebase/storage';
import { db, auth, storage } from './firebase';
import { Project } from './storage';

const PROJECTS_COLLECTION = 'projects';

export async function saveProjectToCloud(project: Project): Promise<void> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const projectRef = doc(db, PROJECTS_COLLECTION, project.id);

    // Prepare project data for Firestore
    // We need to handle undefined values and convert dates to Timestamps if needed
    // For now, we'll keep timestamps as numbers for compatibility
    const projectData = {
        ...project,
        userId: auth.currentUser.uid,
        updatedAt: Date.now(),
        // Ensure nested objects are plain objects
        settings: project.settings || {},
        files: project.files || {},
        thread: project.thread ? {
            ...project.thread,
            // Remove undefined values from thread
            peerHistory: project.thread.peerHistory || null,
            judgeResult: project.thread.judgeResult || null,
        } : null
    };

    // Remove undefined fields to avoid Firestore errors
    const cleanData = JSON.parse(JSON.stringify(projectData));

    await setDoc(projectRef, cleanData, { merge: true });
}

export async function loadProjectFromCloud(projectId: string): Promise<Project | null> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const snapshot = await getDoc(projectRef);

    if (!snapshot.exists()) return null;

    const data = snapshot.data();
    // Verify ownership or shared access
    const isOwner = data.userId === auth.currentUser.uid;
    const isShared = auth.currentUser.email && data.sharedWith?.includes(auth.currentUser.email);

    if (!isOwner && !isShared) {
        throw new Error('Unauthorized access to project');
    }

    return data as Project;
}

export async function listCloudProjects(): Promise<Project[]> {
    if (!auth.currentUser) return [];

    // Query projects owned by user
    const ownerQuery = query(
        collection(db, PROJECTS_COLLECTION),
        where('userId', '==', auth.currentUser.uid),
        orderBy('updatedAt', 'desc')
    );

    // Query projects shared with user (by email)
    // Note: This requires the user's email to be available in auth.currentUser
    let sharedQuery = null;
    if (auth.currentUser.email) {
        sharedQuery = query(
            collection(db, PROJECTS_COLLECTION),
            where('sharedWith', 'array-contains', auth.currentUser.email),
            orderBy('updatedAt', 'desc')
        );
    }

    const [ownerSnapshot, sharedSnapshot] = await Promise.all([
        getDocs(ownerQuery),
        sharedQuery ? getDocs(sharedQuery) : Promise.resolve({ docs: [] })
    ]);

    const projects = [
        ...ownerSnapshot.docs.map(doc => doc.data() as Project),
        ...sharedSnapshot.docs.map(doc => doc.data() as Project)
    ];

    // Remove duplicates (in case logic overlaps, though it shouldn't here)
    const uniqueProjects = Array.from(new Map(projects.map(p => [p.id, p])).values());

    // Sort combined results
    return uniqueProjects.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
}

export async function deleteCloudProject(projectId: string): Promise<void> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    // Verify ownership first
    const project = await loadProjectFromCloud(projectId);
    if (project) {
        // Only owner can delete
        if (project.userId !== auth.currentUser.uid) {
            throw new Error('Only the project owner can delete this project');
        }
        await deleteDoc(doc(db, PROJECTS_COLLECTION, projectId));
    }
}

export async function shareProject(projectId: string, email: string): Promise<void> {
    if (!auth.currentUser) throw new Error('User not authenticated');
    if (!email) throw new Error('Email is required');

    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const snapshot = await getDoc(projectRef);

    if (!snapshot.exists()) throw new Error('Project not found');

    const project = snapshot.data() as Project;

    // Only owner can share
    if (project.userId !== auth.currentUser.uid) {
        throw new Error('Only the project owner can share this project');
    }

    const currentShared = project.sharedWith || [];
    if (!currentShared.includes(email)) {
        await setDoc(projectRef, {
            sharedWith: [...currentShared, email],
            updatedAt: Date.now()
        }, { merge: true });
    }
}

export async function unshareProject(projectId: string, email: string): Promise<void> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const snapshot = await getDoc(projectRef);

    if (!snapshot.exists()) throw new Error('Project not found');

    const project = snapshot.data() as Project;

    // Only owner can unshare
    if (project.userId !== auth.currentUser.uid) {
        throw new Error('Only the project owner can manage sharing');
    }

    const currentShared = project.sharedWith || [];
    const updatedShared = currentShared.filter(e => e !== email);

    await setDoc(projectRef, {
        sharedWith: updatedShared,
        updatedAt: Date.now()
    }, { merge: true });
}

export function subscribeToProject(projectId: string, callback: (project: Project | null) => void) {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);

    return onSnapshot(projectRef, (snapshot) => {
        if (snapshot.exists()) {
            const data = snapshot.data() as Project;
            const user = auth.currentUser;

            // Check access: Owner OR Shared With
            const isOwner = data.userId === user?.uid;
            const isShared = user?.email && data.sharedWith?.includes(user.email);

            if (isOwner || isShared) {
                callback(data);
            } else {
                callback(null);
            }
        } else {
            callback(null);
        }
    });
}

// ============================================================================
// Firebase Storage Functions (Zero-Knowledge File Storage)
// ============================================================================
// Files are stored at: users/{userId}/projects/{projectId}/files/{fileName}
// This ensures each user can only access their own files via security rules

export interface CloudFile {
    name: string;
    path: string;
    url: string;
    size: number;
    contentType: string;
    uploadedAt: number;
}

/**
 * Upload a file to Firebase Storage (Zero-Knowledge)
 * Files are stored under the user's UID to ensure privacy
 */
export async function uploadFileToCloud(
    projectId: string,
    fileName: string,
    fileData: Blob | Uint8Array | ArrayBuffer,
    contentType: string
): Promise<CloudFile> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const userId = auth.currentUser.uid;
    const filePath = `users/${userId}/projects/${projectId}/files/${fileName}`;
    const fileRef = ref(storage, filePath);

    console.log(`[Cloud Storage] Uploading file: ${filePath}`);

    // Upload the file
    const snapshot = await uploadBytes(fileRef, fileData, {
        contentType,
        customMetadata: {
            uploadedBy: userId,
            projectId,
            uploadedAt: Date.now().toString()
        }
    });

    // Get download URL
    const url = await getDownloadURL(fileRef);

    console.log(`[Cloud Storage] ✅ File uploaded: ${fileName}`);

    return {
        name: fileName,
        path: filePath,
        url,
        size: snapshot.metadata.size,
        contentType: snapshot.metadata.contentType || contentType,
        uploadedAt: Date.now()
    };
}

/**
 * Get download URL for a file
 */
export async function getFileDownloadURL(projectId: string, fileName: string): Promise<string> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const userId = auth.currentUser.uid;
    const filePath = `users/${userId}/projects/${projectId}/files/${fileName}`;
    const fileRef = ref(storage, filePath);

    return await getDownloadURL(fileRef);
}

/**
 * Delete a file from cloud storage
 */
export async function deleteFileFromCloud(projectId: string, fileName: string): Promise<void> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const userId = auth.currentUser.uid;
    const filePath = `users/${userId}/projects/${projectId}/files/${fileName}`;
    const fileRef = ref(storage, filePath);

    console.log(`[Cloud Storage] Deleting file: ${filePath}`);
    await deleteObject(fileRef);
    console.log(`[Cloud Storage] ✅ File deleted: ${fileName}`);
}

/**
 * List all files for a project
 */
export async function listProjectFiles(projectId: string): Promise<CloudFile[]> {
    if (!auth.currentUser) throw new Error('User not authenticated');

    const userId = auth.currentUser.uid;
    const folderPath = `users/${userId}/projects/${projectId}/files`;
    const folderRef = ref(storage, folderPath);

    console.log(`[Cloud Storage] Listing files in: ${folderPath}`);

    const result = await listAll(folderRef);
    const files: CloudFile[] = [];

    for (const itemRef of result.items) {
        const metadata = await getMetadata(itemRef);
        const url = await getDownloadURL(itemRef);

        files.push({
            name: itemRef.name,
            path: itemRef.fullPath,
            url,
            size: metadata.size,
            contentType: metadata.contentType || 'application/octet-stream',
            uploadedAt: metadata.customMetadata?.uploadedAt
                ? parseInt(metadata.customMetadata.uploadedAt)
                : metadata.timeCreated
                    ? new Date(metadata.timeCreated).getTime()
                    : Date.now()
        });
    }

    console.log(`[Cloud Storage] Found ${files.length} files`);
    return files.sort((a, b) => b.uploadedAt - a.uploadedAt);
}
